import { Box, Button, CircularProgress } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { ImageIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { uploadPublicFile } from "@/shared/api/uploadFile";
import {
  ExperienceErrorText,
  ExperienceFieldGuide,
  ExperienceHeader,
  ExperienceMediaCard,
  ExperienceShell,
  getExperienceTheme,
  getFloatingActionSx,
} from "./ExperienceUI";
import ImageUpload from "@/shared/components/file-upload/ImageUpload";

interface CoverImageData {
  imageUrl?: string;
}

interface CoverImageExperienceProps {
  darkMode: boolean;
  data?: CoverImageData;
  onDataChange?: (data: CoverImageData) => void;
  template?: string;
  pageId?: string;
  pageType?: string;
}

const normalizePageTypeKey = (type?: string) => (type || "").replace(/_/g, "").toLowerCase().trim();

const isEndingImagePageType = (pageType?: string) => {
  const normalized = normalizePageTypeKey(pageType);
  return normalized === "endingimage" || pageType === "EndingImage";
};

const getValidationSchema = (t: any, isEndingImagePage: boolean) =>
  Yup.object({
    imageUrl: Yup.string().required(
      isEndingImagePage
        ? t("ending_image_is_required", {
            ns: "taascard",
            defaultValue: "Ending image is required",
          })
        : t("cover_image_is_required", { ns: "talent" }) || "Cover image is required"
    ),
  });

const CoverImageExperience: React.FC<CoverImageExperienceProps> = ({
  darkMode,
  data,
  onDataChange,
  template,
  pageId,
  pageType,
}) => {
  const { t } = useTranslation();
  const theme = getExperienceTheme(darkMode);
  const giftCardTemplate = t("gift_card_template", { ns: "taascard" });
  const isGiftCardTemplate = template === giftCardTemplate;
  const singleCardTemplate = t("single_card_template", { ns: "taascard" });
  const kIndieCardTemplate = t("k_indie_card_template", { ns: "taascard" });
  const isSingleOrKIndieTemplate =
    template === singleCardTemplate ||
    template === "Single Card Template" ||
    template === kIndieCardTemplate ||
    template === "K-Indie Card Template";
  const isPage1 = pageId === "1";
  const isEndingImagePage =
    isEndingImagePageType(pageType) || (isSingleOrKIndieTemplate && pageId != null && !isPage1);
  const shouldHideRemoveButton = isGiftCardTemplate && isPage1;
  const shouldShowReplaceButton = isGiftCardTemplate && isPage1;
  const validationSchema = getValidationSchema(t, isEndingImagePage);
  const isInternalUpdateRef = useRef(false);
  const [isUploading, setIsUploading] = useState(false);

  const formik = useFormik<CoverImageData>({
    initialValues: {
      imageUrl: data?.imageUrl ?? "https://picsum.photos/800/400",
    },
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (data && !isInternalUpdateRef.current) {
      const newValues: CoverImageData = {
        imageUrl: data.imageUrl ?? "https://picsum.photos/800/400",
      };
      const hasChanged = Object.keys(newValues).some(
        (key) =>
          formik.values[key as keyof CoverImageData] !== newValues[key as keyof CoverImageData]
      );
      if (hasChanged) {
        formik.setValues(newValues);
      }
    }
    isInternalUpdateRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (onDataChange) {
      const timeoutId = setTimeout(() => {
        isInternalUpdateRef.current = true;
        onDataChange({
          imageUrl: formik.values.imageUrl,
        });
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  const handleFileSelect = async (files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];

      if (shouldShowReplaceButton) {
        setIsUploading(true);
        try {
          const uploadedUrl = await uploadPublicFile(file, "Card");
          formik.setFieldValue("imageUrl", uploadedUrl);
          formik.setFieldError("imageUrl", undefined);
        } catch (error) {
          console.error("Failed to upload image:", error);
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            formik.setFieldValue("imageUrl", result);
            formik.setFieldError("imageUrl", undefined);
          };
          reader.readAsDataURL(file);
        } finally {
          setIsUploading(false);
        }
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          formik.setFieldValue("imageUrl", result);
          formik.setFieldError("imageUrl", undefined);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleRemoveImage = () => {
    formik.setFieldValue("imageUrl", "");
    formik.setFieldTouched("imageUrl", true);
  };

  return (
    <FormikProvider value={formik}>
      <ExperienceShell gap={2}>
        <ExperienceHeader
          darkMode={darkMode}
          icon={ImageIcon}
          title={
            isEndingImagePage
              ? t("ending_image", {
                  ns: "taascard",
                  defaultValue: "Ending image",
                })
              : t("cover_image", { ns: "talent", defaultValue: "Cover image" })
          }
          subtitle={
            isEndingImagePage
              ? "The closing visual fans see when they finish your card — make it memorable."
              : "This is the first impression — make it bold and on-brand."
          }
        />

        <ExperienceFieldGuide darkMode={darkMode}>
          {isEndingImagePage
            ? "Upload the image that closes your single card experience."
            : "Upload the hero image that appears when fans first open your card."}
        </ExperienceFieldGuide>

        {formik.values.imageUrl ? (
          <ExperienceMediaCard
            darkMode={darkMode}
            actions={
              shouldShowReplaceButton ? (
                <>
                  {isUploading && <CircularProgress size={22} sx={{ color: theme.textPrimary }} />}
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileSelect(e.target.files)}
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        opacity: 0,
                        cursor: "pointer",
                        zIndex: 1,
                      }}
                      disabled={isUploading}
                    />
                    <Button
                      variant="contained"
                      disabled={isUploading}
                      sx={getFloatingActionSx(darkMode)}
                    >
                      {isEndingImagePage
                        ? t("replace_ending_image", {
                            ns: "taascard",
                            defaultValue: "Replace ending image",
                          })
                        : t("replace_image", { ns: "talent" }) || "Replace image"}
                    </Button>
                  </Box>
                </>
              ) : (
                !shouldHideRemoveButton && (
                  <Button
                    variant="contained"
                    onClick={handleRemoveImage}
                    sx={getFloatingActionSx(darkMode)}
                  >
                    {isEndingImagePage
                      ? t("remove_ending_image", {
                          ns: "taascard",
                          defaultValue: "Remove ending image",
                        })
                      : t("remove_image", { ns: "talent" }) || "Remove image"}
                  </Button>
                )
              )
            }
          >
            <Box
              sx={{
                width: "100%",
                minHeight: 280,
                backgroundImage: `url(${formik.values.imageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                transition: "transform 0.3s ease",
                "&:hover": { transform: "scale(1.01)" },
              }}
            />
          </ExperienceMediaCard>
        ) : (
          <Box
            sx={{
              width: "100%",
              minHeight: 280,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
              borderRadius: "16px",
              border: `2px dashed ${
                formik.touched.imageUrl && formik.errors.imageUrl
                  ? darkMode
                    ? "var(--destructive-500)"
                    : "var(--destructive-600)"
                  : theme.uploadBorder
              }`,
              bgcolor: theme.uploadBg,
              transition: "all 0.22s ease",
              "&:hover": {
                borderColor: theme.borderHover,
                bgcolor: theme.uploadBgHover,
              },
            }}
          >
            <ImageUpload
              onFileSelect={handleFileSelect}
              accept="image/*"
              multiple={false}
              label={
                isEndingImagePage
                  ? t("drag_and_drop_ending_image_to_upload", {
                      ns: "taascard",
                      defaultValue: "Drag and drop ending image to upload",
                    })
                  : t("drag_and_drop_cover_image_to_upload", {
                      ns: "talent",
                    }) || "Drag and drop cover image to upload"
              }
              subLabel={
                isEndingImagePage
                  ? t("svg_png_jpg_or_gif_recommended", {
                      ns: "taascard",
                      defaultValue: "SVG, PNG, JPG or GIF recommended",
                    })
                  : t("svg_png_jpg_or_gif_max_800x400px", { ns: "talent" }) ||
                    "SVG, PNG, JPG or GIF (max 800x400px)"
              }
              browseButtonLabel={
                isEndingImagePage
                  ? t("browse_ending_image", {
                      ns: "taascard",
                      defaultValue: "Browse ending image",
                    })
                  : t("browse_cover_image", { ns: "talent" }) || "Browse cover image"
              }
              layout="column"
            />
          </Box>
        )}
        {formik.touched.imageUrl && formik.errors.imageUrl && (
          <ExperienceErrorText darkMode={darkMode}>{formik.errors.imageUrl}</ExperienceErrorText>
        )}
      </ExperienceShell>
    </FormikProvider>
  );
};

export default CoverImageExperience;
